import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import type { LessonPlanFolder } from '../types';

interface FolderWithChildren extends LessonPlanFolder {
  children: FolderWithChildren[];
}

interface FolderSelectorProps {
  folders: LessonPlanFolder[];
  value: string | null;
  onChange: (folderId: string | null) => void;
  label?: string;
  allowNone?: boolean;
  noneLabel?: string;
}

/**
 * Component chung để chọn folder với phân cấp đúng
 * Tái sử dụng ở nhiều nơi: Di chuyển giáo án, Tạo folder con, Upload vào folder, etc.
 */
export const FolderSelector: React.FC<FolderSelectorProps> = ({
  folders,
  value,
  onChange,
  label = 'Chọn folder',
  allowNone = true,
  noneLabel = 'Không có folder (gỡ khỏi folder)'
}) => {
  // Build folder tree from flat folders
  const folderTree = React.useMemo(() => {
    const folderMap = new Map<string, FolderWithChildren>();
    const rootFolders: FolderWithChildren[] = [];

    // Initialize all folders with children array
    folders.forEach(folder => {
      folderMap.set(folder.id, { ...folder, children: [] });
    });

    // Build tree structure
    folders.forEach(folder => {
      const folderWithChildren = folderMap.get(folder.id)!;
      if (folder.parentFolderId && folderMap.has(folder.parentFolderId)) {
        const parent = folderMap.get(folder.parentFolderId)!;
        parent.children.push(folderWithChildren);
      } else {
        rootFolders.push(folderWithChildren);
      }
    });

    // Sort folders by name
    const sortFolders = (foldersList: FolderWithChildren[]) => {
      foldersList.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
      foldersList.forEach(f => {
        if (f.children.length > 0) {
          sortFolders(f.children);
        }
      });
    };

    sortFolders(rootFolders);
    return rootFolders;
  }, [folders]);

  // Flatten tree with indentation for display
  const flatFolders = React.useMemo(() => {
    const flatten = (folders: FolderWithChildren[], level: number = 0): Array<{ id: string; name: string; level: number }> => {
      const result: Array<{ id: string; name: string; level: number }> = [];
      folders.forEach(folder => {
        result.push({ id: folder.id, name: folder.name, level });
        if (folder.children.length > 0) {
          result.push(...flatten(folder.children, level + 1));
        }
      });
      return result;
    };
    return flatten(folderTree);
  }, [folderTree]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    onChange(newValue === '' ? null : newValue);
  };

  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || ''}
        onChange={handleChange}
        label={label}
      >
        {allowNone && (
          <MenuItem value="">
            <em>{noneLabel}</em>
          </MenuItem>
        )}
        {flatFolders.map(folder => (
          <MenuItem 
            key={folder.id} 
            value={folder.id}
          >
            {'─'.repeat(folder.level + 1)} {folder.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

